import React, { Fragment, ReactNode, useMemo, useRef } from 'react'

import { useMeasureDirty } from '../../../../hooks/useMeasureDirty'
import { useAdjustedDimensions } from '../../hooks/useAdjustedDimensions'
import { FileComponentProps, FileType, Orientation, PreviewFile, RotatedFiles } from '../../types'
import { FilesPreviewContentProps } from '../FilesPreviewContent'
import { HtmlPreview } from '../HtmlPreview'
import { ImagePreview } from '../ImagePreview'
import { LazyPreview } from '../LazyPreview'
import { PdfReact } from '../PdfReact'
import * as Styled from './styles'

const formatToComponent: Record<FileType, React.MemoExoticComponent<React.ComponentType<FileComponentProps>>> = {
  [FileType.Image]: ImagePreview,
  [FileType.Pdf]: PdfReact,
  [FileType.Html]: HtmlPreview,
}

export interface FileContentPropsType extends FilesPreviewContentProps {
  actionsComponent?: ReactNode
  activeFileType: FileType
  activePdfPage: number
  file: PreviewFile
  handleLoad: (orientation?: Orientation) => void
  onPdfLoad: (arg: number) => void
  orientation: Orientation
  rotatedFiles?: RotatedFiles
}

export const FileContent = ({
  actionsComponent,
  activePdfPage,
  activeFileType,
  aspectRatio: defaultAspectRatio,
  borderRadius,
  className,
  circle,
  file,
  fitToHeight,
  fitToWidth,
  fitType = 'contain',
  fullWidth = true,
  handleLoad,
  lazyLoadOptions,
  margin,
  onError,
  onImageZoomClick,
  onPdfLoad,
  orientation,
  rotatedFiles,
  withAnnotations,
  withLazyLoad,
  withModalZoom,
  withShadow,
  zoomable,
  zoomScale,
}: FileContentPropsType) => {
  const componentRef = useRef<HTMLDivElement>(null)
  const { height: componentHeight = 0, width: componentWidth } = useMeasureDirty(componentRef)
  const FileComponent = formatToComponent[activeFileType]
  const FileComponentContainer = withLazyLoad ? LazyPreview : Fragment
  const fileComponentContainerOptions = withLazyLoad ? { options: lazyLoadOptions } : {}
  const showActions = !!actionsComponent && !zoomable

  const { width, height, aspectRatio } = useAdjustedDimensions({
    aspectRatio: defaultAspectRatio,
    componentHeight,
    componentWidth,
    fitToHeight,
    fitToWidth,
    orientation,
  })

  const currentFileRotation = useMemo(() => rotatedFiles?.[file.src], [file.src, rotatedFiles])

  return (
    <Styled.StickyWrapper
      aspectRatio={aspectRatio}
      borderRadius={borderRadius}
      className={className}
      clickable={withModalZoom}
      fitToHeight={fitToHeight}
      fitToWidth={fitToWidth}
      fitType={fitType}
      fullWidth={fullWidth}
      margin={margin}
      onClick={withModalZoom && onImageZoomClick ? onImageZoomClick : undefined}
      ref={componentRef}
      rotation={currentFileRotation}
      withShadow={withShadow}
    >
      <FileComponentContainer {...fileComponentContainerOptions}>
        <FileComponent
          aspectRatio={aspectRatio}
          activePage={activePdfPage}
          activeFileType={activeFileType}
          alt={file.alt}
          circle={circle}
          fitToHeight={fitToHeight}
          fitToWidth={fitToWidth}
          fitType={fitType}
          height={fitType === 'contain' ? height : 0}
          onError={onError}
          onLoad={handleLoad}
          onPdfLoad={onPdfLoad}
          rotation={currentFileRotation}
          src={file.src}
          width={width}
          withAnnotations={withAnnotations}
          zoomScale={zoomScale}
        />
      </FileComponentContainer>
      {showActions && actionsComponent}
    </Styled.StickyWrapper>
  )
}
